import { ScreenIcon } from "./components/ScreenIcon";
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { magicLossOnPublish, magicLossSay, type AssetSlot } from "@greg-chick/shared";
import {
  loadSlots,
  loadManifest,
  loadBookPages,
  publishAssets,
  contentBase,
  checkToken,
  type Manifest,
  type ManifestEntry,
  type BookPageRef,
} from "./lib/contentApi";
import { ZoneEditor } from "./components/ZoneEditor";
import { BookEditor } from "./components/BookEditor";
import { BookDub } from "./components/BookDub";
import { AssetBackupPanel } from "./components/AssetBackupPanel";
import { SnapshotPanel } from "./components/SnapshotPanel";
import { NoticePanel } from "./components/NoticePanel";
import { ArCardPanel } from "./components/ArCardPanel";
import { ReleaseReady } from "./components/ReleaseReady";
import { MagicPublishCard } from "./components/MagicPublishCard";
import { GuardianTablePanel } from "./components/GuardianTablePanel";
import { MagicHealthPanel } from "./components/MagicHealthPanel";
import { ArTiltPanel } from "./components/ArTiltPanel";
import { RedirectPanel } from "./components/RedirectPanel";
import { SiteTextPanel } from "./components/SiteTextPanel";
import { ZoneImagePanel } from "./components/ZoneImagePanel";
import { AnimationStudio } from "./components/AnimationStudio";
import { CharacterHub } from "./components/CharacterHub";
import { AiKbPanel } from "./components/AiKbPanel";
import { EventRallyTabs } from "./components/EventRallyTabs";
import { AssetsTab } from "./AssetsTab";
import { GoodsManager } from "./components/GoodsManager";
import { SecretsPanel } from "./components/SecretsPanel";
import { StatsPanel } from "./components/StatsPanel";
import { ClassRoomsPanel } from "./components/ClassRoomsPanel";
import { ClassPlanPanel } from "./components/ClassPlanPanel";
import { UiFeaturesPanel } from "./components/UiFeaturesPanel";
import { LivePreview } from "./components/LivePreview";
import { PageHead, SaveBar } from "./components/ui";
import { AssistPanel } from "./components/AssistPanel";
import { CustomResourcePanel } from "./components/CustomResourcePanel";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { ADMIN_GROUPS, ADMIN_SCREENS, adminScreenMatches } from "@greg-chick/shared";
import { assistProviders, notifyAuthFail } from "./lib/contentApi";
import { unsavedCount } from "./lib/saveBar";
import { ContentReach } from "./components/ContentReach";
import { PendingDeploy } from "./components/PendingDeploy";
import { ReloginModal } from "./components/ReloginModal";
import { saveFailSay } from "./lib/saveFail";

const TOKEN_KEY = "gc_admin_token";
// 탭 = 운영자가 하는 일 단위. 이름만으론 무엇을 하는 곳인지 안 보여서 역할 한 줄을 붙인다.
// 메뉴·검색·도우미가 **같은 화면 목록**을 본다(shared 단일 정의) — 화면을 추가하면 셋 다 자동 반영.
const GROUPS = ADMIN_GROUPS;
const TABS = ADMIN_SCREENS;

/** 운영자가 확인창에서 [취소] — 실패 문구로 덮지 않고 「취소했어요」를 그대로 둔다. */
class CancelledPublish extends Error {
  constructor() {
    super("확인창에서 취소 — 안 올렸어요");
  }
}

export function App() {
  const [token, setToken] = useState<string | null>(() => sessionStorage.getItem(TOKEN_KEY));
  if (!token) return <TokenGate onEnter={setToken} />;
  return (
    <Console
      onLogout={() => {
        sessionStorage.removeItem(TOKEN_KEY);
        setToken(null);
      }}
    />
  );
}

// 운영자 토큰 게이트(FR-ADMIN-01). 엣지에 토큰을 검증 후 입장(불일치 차단).
function TokenGate({ onEnter }: { onEnter: (t: string) => void }) {
  const [v, setV] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const t = v.trim();
    if (!t) return;
    setBusy(true);
    setErr(null);
    const result = await checkToken(t);
    setBusy(false);
    if (result === "bad") {
      setErr("토큰이 올바르지 않아요. 게시·저장에 쓰는 운영자 토큰을 입력하세요.");
      return;
    }
    if (result === "busy") {
      setErr("잠깐 너무 많이 시도했어요. 1분 뒤에 다시 눌러 주세요(열쇠가 틀렸다는 뜻은 아니에요).");
      return;
    }
    // ok 또는 offline(엣지 미연결 — 미리보기/편집은 가능) → 입장
    sessionStorage.setItem(TOKEN_KEY, t);
    onEnter(t);
  }

  return (
    <main className="gate">
      <div className="gate__card">
        <h1>📚 그림책방 꾸미기</h1>
        <p>관리자 비밀 열쇠(토큰)를 넣어 주세요.</p>
        <form onSubmit={(e) => void submit(e)}>
          <input
            type="password"
            value={v}
            onChange={(e) => setV(e.target.value)}
            placeholder="비밀 열쇠(토큰)"
            autoFocus
          />
          <button type="submit" className="btn btn--primary" disabled={busy}>
            {busy ? "확인 중…" : "들어가기"}
          </button>
        </form>
        {err ? <p className="gate__err">{err}</p> : null}
        <p className="gate__note">
          열쇠가 맞아야 저장·올리기가 돼요. 서버가 꺼져 있으면 구경·편집만 할 수 있어요.
        </p>
      </div>
    </main>
  );
}

function Console({ onLogout }: { onLogout: () => void }) {
  const [slots, setSlots] = useState<AssetSlot[]>([]);
  // 🔴 처음엔 **아직 안 물어봤다** — `null`. 「0쪽」이라고 말하지 않게(「0」은 「안 셌다」일 수 있다)
  const [pages, setPages] = useState<BookPageRef[] | null>(null);
  const [manifest, setManifest] = useState<Manifest>({ version: 0, assets: {} });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  // 새로고침해도 보던 탭 유지(운영자 UX — 매번 '그림 넣기'로 돌아가지 않게). 미지값은 기본 탭.
  type TabId = (typeof TABS)[number]["id"];
  const [tab, setTabState] = useState<TabId>(() => {
    const saved = localStorage.getItem("gc_admin_tab");
    return TABS.some((t) => t.id === saved) ? (saved as TabId) : "home";
  });
  // 실제 웹 미리보기는 화면(탭)이 아니라 **도구** — 어느 작업 중에도 켜고 끌 수 있게 위에 둔다.
  const [preview, setPreview] = useState(false);
  // 🙋 도우미 — 엣지에 AI 키가 하나도 없으면 버튼 자체를 그리지 않는다(검색은 그대로 동작).
  const [assistOn, setAssistOn] = useState(false);
  const [assistOk, setAssistOk] = useState(false);
  useEffect(() => {
    assistProviders()
      .then((p) => setAssistOk(p.length > 0))
      .catch(() => setAssistOk(false));
  }, []);
  /** 도우미가 "여기예요" 하면 그 화면을 열고, 짚어준 칸으로 스크롤해 잠깐 반짝인다. */
  function goFromAssist(screenId: string, highlight?: string) {
    setTab(screenId as TabId);
    if (!highlight) return;
    window.setTimeout(() => {
      const el = document.querySelector(`[data-slot="${CSS.escape(highlight)}"]`);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("is-pointed");
      window.setTimeout(() => el.classList.remove("is-pointed"), 2400);
    }, 350); // 화면이 그려진 뒤에 찾는다
  }
  /**
   * 화면 옮기기 — **저장 안 한 게 있으면 먼저 물어본다.**
   *
   * 화면을 옮기면 그 패널이 통째로 사라져서 고치던 게 날아간다. 창을 닫는 것보다 훨씬 자주
   * 일어나는 사고인데 여태 아무 경고가 없었다(「출시 준비」의 [채우러 가기]도 이 이동이다).
   * 세는 자리는 저장바 한 곳(`unsavedCount`) — 화면마다 세면 새 화면에서 또 빠뜨린다.
   */
  const setTab = (t: TabId) => {
    const n = unsavedCount();
    if (n > 0 && !window.confirm(`저장하지 않은 게 ${n}개 있어요. 버리고 다른 화면으로 갈까요?`))
      return;
    setTabState(t);
    localStorage.setItem("gc_admin_tab", t);
  };

  // 창을 닫거나 새로고침할 때도 같은 판정으로 붙잡는다(브라우저 기본 문구가 뜬다).
  useEffect(() => {
    const h = (e: BeforeUnloadEvent) => {
      if (unsavedCount() > 0) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, []);
  const [publishMsg, setPublishMsg] = useState<string | null>(null);
  const [pubBusy, setPubBusy] = useState(false); // 저장/게시 진행 중(중복 실행 방지)

  useEffect(() => {
    let alive = true;
    Promise.all([loadSlots(), loadManifest(), loadBookPages()])
      .then(([s, m, p]) => {
        if (!alive) return;
        setSlots(s);
        setManifest(m);
        setPages(p);
        setLoading(false);
        if (s.length === 0)
          setErr(`콘텐츠를 못 불러왔어요(${contentBase()}). 웹 dev 서버가 떠 있나요?`);
      })
      .catch(() => alive && (setErr("불러오기 실패"), setLoading(false)));
    return () => {
      alive = false;
    };
  }, []);

  // 들어올 때 열쇠를 한 번 다시 확인한다 — **저장할 때가 아니라 지금** 알려주려고.
  // 열쇠는 탭마다 따로 산다(sessionStorage). 며칠 열어 둔 탭은 옛 열쇠를 들고 있어서
  // 편집은 다 되는데 저장만 401 이 났다. 그때 알면 이미 한참 고친 뒤다(2026-08-20 실제 신고).
  // offline 이면 아무 말 안 한다 — 서버 없이 편집하는 건 원래 되는 일이다.
  useEffect(() => {
    void checkToken(sessionStorage.getItem(TOKEN_KEY) ?? "").then((r) => {
      if (r === "bad") notifyAuthFail(); // busy(잠깐 막힘)는 열쇠 문제가 아니다 — 창을 띄우지 않는다
    });
  }, []);

  const slotByKey = useMemo(() => Object.fromEntries(slots.map((s) => [s.key, s])), [slots]);

  // 내가 이 화면에서 바꾼 키만 추적 → 게시 시 "게시 직전 최신본 + 내 변경분" 병합(동시 편집 덮어쓰기 방지)
  const [dirty, setDirty] = useState<Set<string>>(new Set());
  /**
   * 💾 [그림 전체]의 보관본 — **바꾼 자리만** 담는다(매니페스트 전체를 담으면 저장소가 찬다).
   * 지운 자리는 null 로 남겨야 「지웠다」가 보존된다.
   */
  const assetDraft = {
    data: () => ({
      entries: Object.fromEntries([...dirty].map((k) => [k, manifest.assets[k] ?? null])),
      dirty: [...dirty],
    }),
    restore: (d: unknown) => {
      const v = d as { entries?: Record<string, ManifestEntry | null> };
      if (!v?.entries) return;
      for (const [k, e] of Object.entries(v.entries)) setEntry(k, e);
    },
  };
  function setEntry(key: string, entry: ManifestEntry | null) {
    setDirty((d) => new Set(d).add(key));
    setManifest((m) => {
      const assets = { ...m.assets };
      if (entry) {
        // 이미지가 교체되면 직전 URL 을 prev 로 보존(1클릭 롤백 — 해시 파일이라 이전 파일은 남아 있음)
        const old = m.assets[key];
        if (old?.url && entry.url && old.url !== entry.url && !entry.prev) {
          entry = { ...entry, prev: old.url };
        }
        assets[key] = entry;
      } else delete assets[key];
      return { ...m, assets };
    });
  }

  const filled = Object.keys(manifest.assets).length;

  function exportManifest() {
    const out: Manifest = {
      version: (manifest.version ?? 0) + 1,
      updatedAt: new Date().toISOString(),
      assets: manifest.assets,
    };
    const blob = new Blob([JSON.stringify(out, null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "asset-manifest.json";
    a.click();
    URL.revokeObjectURL(a.href);
  }

  // 게시(FR-ADMIN-06): "게시 직전 최신 게시본"을 다시 불러와 **내가 바꾼 키만** 얹어 게시.
  //  → 다른 창/운영자·다른 탭(애니메이션 등)의 변경을 덮어쓰지 않음(last-write-wins 제거).
  //  confirm 에 변경 diff(추가/변경/삭제 키)를 보여줘 실수 방지. ⚠️ 별도 "저장"(임시보관)은 없음 —
  //  엣지에 쓰는 순간 라이브 반영이므로 버튼을 게시 하나로 통일(임시보관은 "내보내기"=JSON 백업).
  /**
   * 🔴 **못 올렸으면 던진다** (2026-09-18) — 저장 바는 던지지 않으면 「✅ 웹에 올렸어요」를 띄운다.
   * 확인창 [취소]·다른 곳이 먼저 올림(409)·열쇠 만료·연결 실패에서 조용히 끝나던 것을 고쳤다
   * (체험공간 배치에서 같은 모양으로 「벽을 올렸는데 안 올라갔다」가 났다 — ZoneEditor onPublish).
   */
  async function publish() {
    if (pubBusy) throw new Error("이미 올리는 중이에요");
    if (dirty.size === 0) {
      setPublishMsg("바꾼 항목이 없어요.");
      return;
    }
    setPubBusy(true);
    setPublishMsg("올릴 준비 중…");
    try {
      const fresh = await loadManifest(); // 게시 직전 최신본(다른 편집 반영)
      const next = { ...fresh.assets };
      const added: string[] = [];
      const changed: string[] = [];
      const removed: string[] = [];
      for (const k of dirty) {
        const mine = manifest.assets[k];
        if (mine) {
          (fresh.assets[k] ? changed : added).push(k);
          next[k] = mine;
        } else if (fresh.assets[k]) {
          removed.push(k);
          delete next[k];
        }
      }
      const diff = [
        added.length ? `추가 ${added.length}: ${added.join(", ")}` : "",
        changed.length ? `변경 ${changed.length}: ${changed.join(", ")}` : "",
        removed.length ? `삭제 ${removed.length}: ${removed.join(", ")}` : "",
      ]
        .filter(Boolean)
        .join("\n");
      /**
       * 🪄 **이 게시로 마법이 사라지나** — 마지막 문지기 (2026-09-10 운영 사고).
       *
       * 7쪽 마법이 아무 예고 없이 게시본에서 사라졌다. 카메라 타깃을 다시 외우게 하는 단추가
       * 그 자리를 통째로 갈아 끼우고 있었다(그 자리에 `interact` 가 같이 산다). 그 한 줄은
       * 고쳤지만 **자리를 통째로 쓰는 코드는 또 생긴다** — 같은 자리에 쓰는 곳이 이미 넷이다.
       * 그래서 올리기 직전에 한 번 더 세고, 사라지는 게 있으면 **먼저 그것부터** 보여 준다.
       * 판정은 shared 한 곳(`magicLossOnPublish`).
       */
      const lossSay = magicLossSay(magicLossOnPublish(fresh.assets, next), (id) => `${id} 쪽`);
      setPubBusy(false);
      if (
        !window.confirm(
          (lossSay ? `${lossSay}\n\n` : "") +
            `아래 바뀐 것을 웹에 올릴까요? 보고 있는 모든 사람에게 바로 보여요.\n\n${diff || "(변경 없음)"}`,
        )
      ) {
        setPublishMsg("⏸️ 안 올렸어요 — 확인창에서 [취소]를 눌렀어요. 바꾼 건 그대로예요.");
        throw new CancelledPublish();
      }
      setPubBusy(true);
      setPublishMsg("웹에 올리는 중…");
      const out = await publishAssets(next);
      setManifest({ version: out.version, assets: next });
      setDirty(new Set());
      setPublishMsg(`✅ 다 올렸어요! (v${out.version} · ${out.count}개) — 웹에 바로 보여요`);
    } catch (e) {
      setPubBusy(false);
      if (e instanceof CancelledPublish) throw e;
      /**
       * 🔴 **이유를 삼키지 않는다.** 예전엔 무엇이 잘못됐든 「서버가 켜져 있는지 확인해
       * 주세요」였다 — 다른 곳이 먼저 올려서 막힌 것도 서버가 꺼진 것처럼 보였다.
       * 그러면 운영자는 엉뚱한 데를 본다.
       *
       * 숫자만 있는 메시지(HTTP 상태)는 사람이 읽을 말이 아니니 그때만 바꿔 준다.
       */
      const say = saveFailSay(e, "올리기");
      setPublishMsg(say);
      throw new Error(say); // 저장 바가 「⚠️ 못 올린 것」으로 알게
    }
    setPubBusy(false);
  }

  if (loading)
    return (
      <main className="console">
        <p>불러오는 중…</p>
      </main>
    );

  // [기타 이미지] = **딱 한 장짜리 슬롯만**. 여러 장(프리셋·쪽별·존별)은 각자 전용 화면에서 관리한다.
  //
  // 예전엔 여기가 "이건 빼고, 저것도 빼고" 손으로 적는 제외 목록이었다. 슬롯을 새로 추가할 때마다
  // 여기도 같이 고쳐야 했고, 안 고치면 `<key>.default` 라는 **아무도 안 읽는 유령 칸**이 하나 생겼다.
  // 실제로 그렇게 여덟 칸이 쌓여서 [기타 이미지]가 영영 2/10 이었다.
  // 이제 판단 기준은 슬롯 정의(content/asset-slots.json)의 `repeat` 하나다.
  const singleSlots = slots.filter((s) => (s.repeat ?? "single") === "single");

  return (
    <main className="console">
      {/* 저장이 401 로 막히면 화면을 그대로 둔 채 열쇠만 바꿔 끼운다(고친 내용 보존) */}
      <ReloginModal />
      {/* 웹이 꺼져 있으면 그림이 통째로 안 나온다 — 원인을 화면에 적어 준다 */}
      <ContentReach />
      {/* 여기서 올린 그림이 배포에 안 들어가는 상태면 알려 준다 — 화면에서는 멀쩡히 보인다 */}
      <PendingDeploy />
      <header className="console__top">
        <div>
          <h1>📚 그림책방 꾸미기</h1>
          <p className="console__stat">
            넣어둔 그림 {filled}개 · 웹 주소 {contentBase()}
          </p>
        </div>
        <div className="console__actions">
          {/* 고치는 곳이 아니라 확인하는 도구 — 어느 화면에서 작업하다가도 바로 볼 수 있게 상단 고정 */}
          <button
            type="button"
            className={`btn${preview ? " btn--primary" : ""}`}
            onClick={() => setPreview((v) => !v)}
            title="아이들이 보게 될 실제 화면"
          >
            👀 현재 웹 보기
          </button>
          {assistOk ? (
            <button
              type="button"
              className={`btn${assistOn ? " btn--primary" : ""}`}
              onClick={() => setAssistOn((v) => !v)}
              title="어디서 고치는지 물어보세요"
            >
              🙋 도우미
            </button>
          ) : null}
          <button type="button" className="btn" onClick={onLogout}>
            로그아웃
          </button>
        </div>
      </header>
      {publishMsg ? <p className="console__publish">{publishMsg}</p> : null}

      {preview ? <LivePreview /> : null}

      <div className="console__body" data-assist={assistOn || undefined}>
        <AdminNav tab={tab} onPick={setTab} />
        <div className="console__main">
          {/* 제목은 여기 한 번만. 예전엔 사이드바·설명줄·패널 h2 로 세 번 나왔다. */}
          {(() => {
            const t = TABS.find((x) => x.id === tab);
            return t ? <PageHead icon={t.icon} art={t.art} title={t.label} desc={t.desc} /> : null;
          })()}
          <ErrorBoundary resetKey={tab}>
            {tab === "home" ? (
              <>
                <SiteTextPanel />
                {/* 글과 그림이 다른 탭에 있으면 한 번 고치는 데 탭을 오간다 — 같은 화면에 둔다.
              위치 드래그·애니 같은 고급 편집은 [그림 전체 목록]에 그대로 남아 있다. */}
                <AssetsTab
                  only="main"
                  err={err}
                  slots={slots}
                  slotByKey={slotByKey}
                  pages={pages}
                  manifest={manifest}
                  setEntry={setEntry}
                  singleSlots={singleSlots}
                  dirtyCount={dirty.size}
                  onPublish={publish}
                  onExport={exportManifest}
                  draft={assetDraft}
                />
              </>
            ) : tab === "zones" ? (
              <ZoneEditor />
            ) : tab === "book" ? (
              <BookEditor />
            ) : tab === "anim" ? (
              <AnimationStudio onGoZones={() => setTab("zones")} />
            ) : tab === "dub" ? (
              <BookDub onGoBook={() => setTab("book")} />
            ) : tab === "goods" ? (
              <GoodsManager />
            ) : tab === "rally" ? (
              <EventRallyTabs />
            ) : tab === "aikb" ? (
              <AiKbPanel />
            ) : tab === "custom" ? (
              <CustomResourcePanel onGoDeco={() => setTab("zones")} />
            ) : tab === "ready" ? (
              <>
                {/* 🪄 **만들어 뒀는데 아이 화면에 안 나가는 마법** — 여기서 바로 올린다.
                    예전엔 명령줄(`pnpm magic:seed`)뿐이라 운영 열쇠를 든 개발자만 할 수 있었다.
                    🔴 `ReleaseReady` **안**이 아니라 옆에 둔다 — 그 화면은 열 곳을 한꺼번에 물어보는
                    무거운 컴포넌트라 검사가 배관을 통째로 흉내 내고 있다. 안에 넣으면 그 흉내에
                    없는 손잡이를 부르며 검사 열한 개가 터진다(실제로 그랬다). */}
                <MagicPublishCard />
                <ReleaseReady onGo={(s) => setTab(s as TabId)} />
              </>
            ) : tab === "artilt" ? (
              <ArTiltPanel />
            ) : tab === "magic" ? (
              <>
                <MagicHealthPanel onGoBook={() => setTab("book")} onGoCard={() => setTab("arcard")} />
                <GuardianTablePanel />
              </>
            ) : tab === "arcard" ? (
              <ArCardPanel />
            ) : tab === "notice" ? (
              <NoticePanel />
            ) : tab === "chars" ? (
              <CharacterHub onGoAssets={() => setTab("assets")} />
            ) : tab === "qr" ? (
              <RedirectPanel />
            ) : tab === "zoneimg" ? (
              <ZoneImagePanel />
            ) : tab === "classes" ? (
              <ClassRoomsPanel />
            ) : tab === "classplan" ? (
              <ClassPlanPanel />
            ) : tab === "uifeat" ? (
              <UiFeaturesPanel />
            ) : tab === "stats" ? (
              <StatsPanel />
            ) : tab === "env" ? (
              <>
                <SnapshotPanel />
                <AssetBackupPanel />
                <SecretsPanel />
              </>
            ) : (
              <AssetsTab
                err={err}
                slots={slots}
                slotByKey={slotByKey}
                pages={pages}
                manifest={manifest}
                setEntry={setEntry}
                singleSlots={singleSlots}
                dirtyCount={dirty.size}
                onPublish={publish}
                onExport={exportManifest}
                draft={assetDraft}
              />
            )}
          </ErrorBoundary>
          {/* 저장/게시는 화면이 달라도 같은 자리·같은 버튼. 패널들이 등록한 것을 모아 하나로. */}
          <SaveBar />
        </div>
        {assistOn ? (
          <AssistPanel screen={tab} onGo={goFromAssist} onClose={() => setAssistOn(false)} />
        ) : null}
      </div>
    </main>
  );
}

/**
 * 왼쪽 메뉴 — 4묶음 + "어디서 바꾸지?" 검색.
 * 검색은 이름뿐 아니라 find(별칭: 첨성대·스마트스토어·스탬프…)까지 훑는다.
 * 운영자는 화면 이름을 모르고 **고치고 싶은 대상**을 안다.
 */
function AdminNav({ tab, onPick }: { tab: string; onPick: (id: string) => void }) {
  const [q, setQ] = useState("");
  const hit = (t: (typeof TABS)[number]) => adminScreenMatches(t, q);
  const groups = GROUPS.map((g) => ({
    ...g,
    items: TABS.filter((t) => t.group === g.id && hit(t)),
  })).filter((g) => g.items.length > 0);

  return (
    <nav className="anav" aria-label="작업 선택">
      <input
        className="anav__find"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="🔍 어디서 바꾸지?"
        aria-label="메뉴 검색"
      />
      {groups.map((g) => (
        <div key={g.id} className="anav__group">
          <h2 className="anav__gh">
            <span aria-hidden="true">{g.icon}</span> {g.label}
            <em>{g.hint}</em>
          </h2>
          {g.items.map((t) => (
            <button
              key={t.id}
              type="button"
              className={`anav__item${tab === t.id ? " on" : ""}`}
              onClick={() => onPick(t.id)}
              title={t.desc}
            >
              <ScreenIcon art={t.art} emoji={t.icon} size={22} className="anav__ico" />
              <span className="anav__label">{t.label}</span>
            </button>
          ))}
        </div>
      ))}
      {groups.length === 0 ? (
        <p className="anav__none">그런 화면은 없어요. 다른 말로 찾아보세요.</p>
      ) : null}
    </nav>
  );
}
